import { supabase } from "@/integrations/supabase/client";
import { UserService } from "@/services/users/UserService";
import { Conversation } from "@/types/conversation/conversation";

export class EmailService {
  static async sendEmail(
    to: string,
    subject: string,
    html: string,
    text?: string,
    context?: { teamInvitationId?: string; teamId?: number },
  ) {
    const { data, error } = await supabase.functions.invoke("send-postmark", {
      body: {
        to,
        subject,
        html,
        text,
        ...(context?.teamInvitationId && {
          teamInvitationId: context.teamInvitationId,
          teamId: context.teamId,
        }),
      },
    });

    if (error) throw error;
    if (data?.success !== true || data?.deliveryState !== "delivered") {
      const deliveryError = new Error(
        data?.error || "Email delivery could not be confirmed",
      );
      Object.assign(deliveryError, {
        deliveryState: data?.deliveryState || "unknown",
      });
      throw deliveryError;
    }
    return data;
  }

  static async sendNotification(email: string, title: string, content: string) {
    return this.sendEmail(
      email,
      title,
      `<h2>${title}</h2><p>${content}</p>`,
      content,
    );
  }

  static async sendAnalysisEmail(conversation: Conversation) {
    let workingConversation = { ...conversation };

    const { data: agent, error: agentError } = await supabase
      .from("agents")
      .select("*")
      .eq("id", workingConversation.agent_id)
      .single();

    // Fetch agent data if support team emails are not present
    if (
      !(workingConversation.agent?.settings as any)?.support_team_emails &&
      workingConversation.agent_id
    ) {
      if (!agentError && agent) {
        workingConversation.agent = {
          ...workingConversation.agent,
          ...agent,
        };
      }
    }

    // Fetch conversation_details if analysis results are not present
    if (
      !workingConversation.details?.analysis_result &&
      workingConversation.id
    ) {
      const { data: conversationDetails, error } = await supabase
        .from("conversation_details")
        .select("*")
        .eq("conversation_id", workingConversation.id)
        .single();

      if (!error && conversationDetails) {
        workingConversation.details = {
          ...workingConversation.details,
          ...conversationDetails,
        };
      }
    }

    // Determine email recipients
    let recipients: string[] = [];
    // report_recipients is what the genie's Conversation Reports card manages
    // and what the Actions tab lists; settings.support_team_emails is the
    // older home of the same list.
    const reportRecipients = (workingConversation.agent as any)?.report_recipients || [];
    const supportTeamEmails =
      reportRecipients.length > 0
        ? reportRecipients
        : (workingConversation.agent?.settings as any)?.support_team_emails || [];

    if (supportTeamEmails.length > 0) {
      recipients = supportTeamEmails.filter(
        (email: string) => email && email.trim(),
      );
    }

    // If no support team emails, fall back to authenticated user email
    if (recipients.length === 0) {
      const {
        data: { user },
        error,
      } = await UserService.getUser();
      if (error || !user?.email) {
        throw new Error(
          "No recipients found: no support team emails configured and unable to get user email",
        );
      }
      recipients = [user.email];
    }

    // Prepare data for edge function
    const analysisResult = workingConversation.details?.analysis_result;
    const topics =
      analysisResult?.main_topics || workingConversation.details?.topics || [];
    const insights = analysisResult?.key_insights || [];
    const transcript =
      workingConversation.details?.transcript?.map((item: any) => ({
        role: item.speaker || item.role,
        message: item.text || item.message,
        time_in_call_secs: item.timestamp || item.time_in_call_secs,
        tool_calls: item.tool_calls || [],
      })) || [];

    // Call edge function to send emails
    const { data, error } = await supabase.functions.invoke("email-handler", {
      body: {
        action: "conversationReport",
        recipients,
        conversation: workingConversation,
        aiResult: analysisResult,
        topics,
        insights,
        transcript,
        userId: agent.user_id,
        agentId: workingConversation.agent_id, // Pass agent ID for template selection
      },
    });

    if (error) throw error;

    return data;
  }
}
